import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MultilanguageTextInputProps } from "@sinch/components/form/MultilanguageTextInput/MultilanguageTextInput";

type UseLanguageTextInputProps = {
  selectedLang: string;
  languages: MultilanguageTextInputProps["languages"];
  value: MultilanguageTextInputProps["value"];
  onChange: MultilanguageTextInputProps["onChange"];
  resetLangSelection: () => void;
};

export const useLanguageTextInput = ({
  selectedLang,
  languages,
  value,
  onChange,
  resetLangSelection,
}: UseLanguageTextInputProps) => {
  const [focused, setFocused] = useState<boolean>(false);
  const langMatchWithSelected = useCallback(
    (item: { language: string }) => item.language === selectedLang,
    [selectedLang]
  );

  // Validate selectedLang when languages array changes
  useEffect(() => {
    const isValidLang = languages.some(langMatchWithSelected);
    if (!isValidLang) {
      // If current selection is invalid, fall back to initial language
      resetLangSelection();
    }
  }, [languages, selectedLang, resetLangSelection, langMatchWithSelected]);

  // Get current value for selected language - memoized to avoid recalculation
  const currentValue = useMemo(() => {
    const found = value?.find(langMatchWithSelected);
    return found?.value ?? "";
  }, [value, langMatchWithSelected]);

  // Optimized handleChange that doesn't depend on the value array
  const handleChange = useCallback<React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>>(
    (e) => {
      if (!onChange) return;

      const newValue = e.target.value;
      const existingIndex = value?.findIndex(langMatchWithSelected) ?? -1;

      if (existingIndex > -1) {
        // Update existing language entry
        const newVal = [...(value ?? [])];
        newVal[existingIndex] = { language: selectedLang, value: newValue };
        onChange(newVal);
      } else {
        // Add new language entry
        onChange([...(value ?? []), { language: selectedLang, value: newValue }]);
      }
    },
    [langMatchWithSelected, onChange, selectedLang, value]
  );

  // Memoized handlers to prevent unnecessary re-renders
  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(() => setFocused(false), []);

  return { onFocus: handleFocus, onBlur: handleBlur, focused, onChange: handleChange, value: currentValue };
};
